/**
 * 输入区的纯逻辑：TeamBar 该显示哪一种、`@` 能叫到谁、草稿归谁保管。
 *
 * 这些放在纯函数里而不是组件里，是因为它们每一条都对应一句验收，
 * 而验收要的是「违反它会红」。一个只能靠渲染整棵树才验得到的规则，
 * 变异验证时很容易验的是渲染，不是规则本身。
 */
import type { SubAgentRun, TeamMember } from './roster'
import { mentionable, resolveDisplayName, type HumanMember } from './roster'

/** TeamBar 的两种形态。设计稿 §5：互斥，不存在两条同时在。 */
export type TeamBarShape =
  | { readonly kind: 'solo'; readonly agent: string }
  | { readonly kind: 'team'; readonly count: number }
  | { readonly kind: 'none' }

/**
 * 决定输入框上方那一条长什么样。
 *
 * **拿不到 mode 时回 `none`，不猜。** 猜成 solo 会写出一个不存在的
 * Agent 名字，猜成 team 会写出一个人数——两种都是在界面上断言一件
 * 我们并不知道的事。
 *
 * 团队会话读不到成员（`members === null`）同样回 `none`：
 * 「团队：4 位」里那个 4 是数出来的，数不出来就别写。
 */
export function teamBarShape(input: {
  readonly mode: 'solo' | 'team' | null
  readonly members: readonly TeamMember[] | null
  readonly soloAgentName: string | null
}): TeamBarShape {
  if (input.mode === 'solo') {
    return input.soloAgentName ? { kind: 'solo', agent: input.soloAgentName } : { kind: 'none' }
  }
  if (input.mode === 'team') {
    if (input.members === null) return { kind: 'none' }
    return { kind: 'team', count: mentionable(input.members).length }
  }
  return { kind: 'none' }
}

/**
 * `@` 面板里能点到谁。
 *
 * 签名收 `TeamMember[]`，**收不下 `SubAgentRun`** —— 与 `mentionable`
 * 同一个理由：类型层面挡住「子 Agent 混进 @ 名单」，比运行时过滤可靠，
 * 因为运行时过滤会在有人加一条新来源时悄悄失效。
 *
 * `query` 为空时列全部。匹配只看名字，**不看模型 id**：
 * 按模型 id 能搜到人，等于承认模型是他的身份。
 */
export function mentionCandidates(
  members: readonly TeamMember[] | null,
  query: string,
): readonly TeamMember[] {
  if (members === null) return []
  const all = mentionable(members)
  const q = query.trim().toLowerCase()
  if (q === '') return all
  return all.filter((m) => {
    // 名字解不出来的成员照样可被叫到——用它的职责去匹配。
    // 这里绝不回落到 modelId，理由与 resolveDisplayName 里那段相同。
    const label = resolveDisplayName(m)
    return label !== null && label.toLowerCase().includes(q)
  })
}

/**
 * `@` 面板里能点到哪些**人**。
 *
 * ------------------------------------------------------------------
 * # 为什么与 mentionCandidates 分开，而不是合成一个列表
 *
 * 因为 @ 他们的后果完全不同（PRD §6）：
 *
 *	@ 一位 AI   它用自己的语气回话；要动手时开一件活
 *	@ 一个人     **纯通知**，把他叫来看，不产生任何执行
 *
 * 合成一个列表之后，这两件事只能靠调用处的 `if` 分开——而那种 `if`
 * 在有人加一条新来源时会悄悄失效。分成两个函数，是让「它俩不是一种东西」
 * 在类型上就成立，与 `mentionable` 收不下 `SubAgentRun` 是同一个做法。
 *
 * 匹配只看名字。
 * ------------------------------------------------------------------
 */
export function mentionPeople(
  people: readonly HumanMember[] | null | undefined,
  query: string,
): readonly HumanMember[] {
  if (people === null || people === undefined) return []
  const q = query.trim().toLowerCase()
  if (q === '') return people
  return people.filter((p) => p.name.toLowerCase().includes(q))
}

/**
 * `@` 面板里「还没进这条会话的人」那一组。
 *
 * 与 `mentionPeople` 分开：那一组点一下只是插字，这一组点一下**先叫人进来**。
 * 两件事的后果不同（一个改会话成员，一个不改），所以不合成一个列表——
 * 与 Agent 那侧「在席 / 库里还有」分两组是同一个做法。
 */
export function mentionInvitablePeople<T extends { readonly label: string }>(
  list: readonly T[] | null | undefined,
  query: string,
): readonly T[] {
  if (list === null || list === undefined) return []
  const q = query.trim().toLowerCase()
  if (q === '') return list
  return list.filter((p) => p.label.toLowerCase().includes(q))
}

/**
 * 把一次 `@` 选择写回输入框。回的是新文本与新光标位置。
 *
 * 只替换**触发这次面板的那个 `@` 片段**，不动别处：用户可能已经
 * 叫过别人，把整段重写会把之前的 @ 一起吃掉。
 */
export function applyMention(input: {
  readonly text: string
  readonly caret: number
  readonly name: string
}): { readonly text: string; readonly caret: number } {
  const before = input.text.slice(0, input.caret)
  const at = before.lastIndexOf('@')
  if (at < 0) return { text: input.text, caret: input.caret }
  const inserted = `@${input.name} `
  const text = input.text.slice(0, at) + inserted + input.text.slice(input.caret)
  return { text, caret: at + inserted.length }
}

/** 一条结构化提及。与服务端 `conversations.Mention` 同形。 */
export interface DerivedMention {
  readonly executor_id: string
  readonly offset: number
  readonly length: number
}

const utf8 = new TextEncoder()

/** 一段文本占多少**字节**。见下面 deriveMentions 里那段。 */
function byteLength(text: string): number {
  return utf8.encode(text).length
}

/**
 * 从他打出来的这句话里，认出他 @ 了谁。
 *
 * ------------------------------------------------------------------
 * # 在这个函数之前，`@` 是纯装饰
 *
 * `applyMention` 只往文本框里插了「@小柯 」几个字，发出去的请求体里
 * **没有 mentions 这个字段**。而服务端存得下它、`ValidateMentions` 照常
 * 工作、@ 面板照常弹出来——一个从头到尾不报错的装饰功能。
 * 2026-09-06 arron 加了一位叫 buger 的 Agent、@ 了他，回话的是 Lulu，
 * 开口就是「我是 Lulu」。
 *
 * # 为什么从文本重新推，而不是记着他点过谁
 *
 * 他点完之后还会改：删掉、重打、把名字改半截。一份「点过谁」的清单
 * 会与文本框里的字**慢慢分家**，而分家的表现是「我明明删了他还是被叫到了」。
 * 从文本推是幂等的：屏幕上有什么，发出去的就是什么。
 *
 * # 偏移量是**字节**，不是 JS 的字符下标
 *
 * 服务端那道校验写的是 `m.Offset+m.Length > len(body)`，而 Go 的 `len`
 * 数的是字节。「@小柯」在 JS 里长 3，在 UTF-8 里占 7 个字节。
 * 按 JS 下标发出去的话，短消息里它「碰巧」通得过，长一点的就会被判
 * 「extends past the message body」——**一条时灵时不灵的校验**，
 * 而它不灵的那次表现是整条消息发不出去。
 *
 * # 长名字优先匹配
 *
 * 「小柯」与「小柯二号」同时在名单里时，先试长的：反过来会把
 * 「@小柯二号」认成「@小柯」，然后把后面「二号」两个字留在正文里。
 * ------------------------------------------------------------------
 */
export function deriveMentions(
  text: string,
  members: readonly TeamMember[] | null,
  people?: readonly HumanMember[] | null,
): readonly DerivedMention[] {
  // # 人也要认
  //
  // 这个函数是 2026-09-06 buger 那次之后加的，而**当时只加了 Agent**：
  // executor_id 写死成 `agent:${id}`，people 这个参数压根不存在。
  // 于是 2026-09-08 arron @ 了一位老板，那三个字**又只是三个字**——
  // 同一个形状、同一个函数、隔了两天。
  //
  // 两类候选放进同一个池子按名字长度排：一个人和一位 Agent 可能同名，
  // 而「@ 谁」这件事上先匹配到哪个都算对——**先匹配到长的**才是那条
  // 真正要守的规矩（否则「@小柯二号」会被认成「@小柯」）。
  const agentPool = (members ?? []).map((m) => ({
    kind: 'agent' as const,
    id: m.id,
    label: resolveDisplayName(m),
  }))
  const peoplePool = (people ?? []).map((p) => ({
    kind: 'human' as const,
    id: p.id,
    label: p.name,
  }))
  const named = [...agentPool, ...peoplePool]
    .filter((m): m is { kind: 'agent' | 'human'; id: string; label: string } =>
      m.label !== null && m.label !== '',
    )
    // 长的排前面，见上面那段。
    .sort((a, b) => b.label.length - a.label.length)
  if (named.length === 0) return []

  const out: DerivedMention[] = []
  const taken = new Set<string>()
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== '@') continue
    const rest = text.slice(i + 1)
    const hit = named.find((m) => rest.startsWith(m.label))
    if (hit === undefined) continue
    // **同一位只算一次。** 服务端对重复的 executor_id 直接拒整条消息
    // （ValidateMentions 里那条 duplicate executor id），
    // 而一句话里叫两遍同一个人是很自然的写法。
    // 前缀是两边共用的判据：Agent 走 `agent:`，人走 `user:`
    // （服务端 stewardplan.AgentMentionPrefix 与
    // conversations.HumanMentionPrefix 是对称的一对）。
    // **写死一种的那天，另一种的 @ 就静静地不存在了。**
    const executorID = hit.kind === 'human' ? `user:${hit.id}` : `agent:${hit.id}`
    if (taken.has(executorID)) continue
    taken.add(executorID)
    out.push({
      executor_id: executorID,
      offset: byteLength(text.slice(0, i)),
      length: byteLength(`@${hit.label}`),
    })
  }
  return out
}

/**
 * 光标处是不是正在打一个 `@`。是的话回那个已打出来的片段。
 *
 * `@` 后面出现空格就不再算——「@小柯 你看」里的「你看」不该继续
 * 当搜索词，否则面板会在用户正常打字时一直挂着。
 */
export function activeMentionQuery(text: string, caret: number): string | null {
  const before = text.slice(0, caret)
  const at = before.lastIndexOf('@')
  if (at < 0) return null
  const frag = before.slice(at + 1)
  if (frag.includes(' ') || frag.includes('\n')) return null
  return frag
}

/**
 * 草稿按会话存。
 *
 * **key 里带 conversationId**，因为草稿跟着会话走：切到别的会话
 * 再切回来，用户期望看到的是他在**这条**会话里打了一半的那句话。
 * 用一个全局 key 的话，两条会话会互相覆盖，而表现是「我的字没了」。
 *
 * `null` 会话（还没建出来）也给一个位置，Z12 那条要求
 * 「重试时草稿原样带入新会话」——草稿必须比会话活得久。
 */
export function draftKey(conversationId: string | null): string {
  return `npc.lulu.draft.${conversationId ?? 'pending'}`
}

/** 子 Agent 永远不进 @ 名单。这个常量存在是为了让下面那条用例有东西可指。 */
export const SUB_AGENTS_ARE_NOT_MENTIONABLE: (runs: readonly SubAgentRun[]) => 0 = () => 0

/**
 * 输入框该显示哪句提示语。
 *
 * 设计稿一共 14 种，**这里只做能从真实状态推出来的四种**：
 * 断线 / 挂了项目 / 通用且还没说过话 / 通用且已经在聊。
 *
 * 其余十种依赖 Lulu 当前的对话阶段，而那个信号后端给不出来。
 * 编一个出来比空着糟——一句「补充要求，或者直接说"开始"」会让人以为
 * Lulu 已经把目标整理好了，而它可能什么都没做。
 *
 * 断线排在最前面：那一档要说的不是「说点什么」，是「你说的话不会丢」，
 * 而那句话在别的档上都不成立。
 */
export function composerPlaceholder(input: {
  readonly link: 'live' | 'down'
  readonly projectId: string | null
  readonly messageCount: number
}): 'lulu_workbench.composer.ph_offline' | 'lulu_workbench.composer.ph_project' | 'lulu_workbench.composer.ph_first' | 'lulu_workbench.composer.ph_more' {
  if (input.link === 'down') return 'lulu_workbench.composer.ph_offline'
  if (input.projectId !== null) return 'lulu_workbench.composer.ph_project'
  return input.messageCount === 0
    ? 'lulu_workbench.composer.ph_first'
    : 'lulu_workbench.composer.ph_more'
}
